import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { DeserializedUser } from '@api';

import { useSelfKarmaQuery, useUserSqrvBalance } from '@query/karma';

import { BrandIcon } from '@ui/uikit/components/common/BrandIcon';
import { PriceDetails } from '@ui/uikit/components/common/PriceDetails';
import { Karma } from '@ui/uikit/components/icons/mono/Karma';
import { Sqr as MonoSqr } from '@ui/uikit/components/icons/mono/Sqr';
import { BackgroundSQR, USDT } from '@ui/uikit/components/icons/poly';
import { formatReward } from '@ui/uikit/utils';

interface ProfileMenuBalanceItem {
  description: ReactNode;
  value: string;
}

interface ProfileMenuBalanceProps {
  user?: DeserializedUser | null;
}

export const ProfileMenuBalance = ({ user }: ProfileMenuBalanceProps) => {
  const { data: userKarma, isLoading: isLoadingUserKarma } = useSelfKarmaQuery(
    {
      searchParams: { period: 'daily' },
    },
    { enabled: Boolean(user) },
  );

  const { data: userSqrv, isLoading: isLoadingUserSqrv } = useUserSqrvBalance();

  const isLoading = isLoadingUserKarma && isLoadingUserSqrv;

  const items: ProfileMenuBalanceItem[] = useMemo(() => {
    if (!user) {
      return [];
    }

    return [
      {
        description: (
          <BrandIcon size="extra-small" icon={BackgroundSQR} label="SQR" labelColor="tertiary" />
        ),
        value: formatReward(userKarma?.totalReward ?? 0),
        dataQa: 'profile-balance-data-sqr',
      },
      {
        description: (
          <BrandIcon size="extra-small" icon={USDT} label="USDT" labelColor="tertiary" />
        ),
        value: formatReward(userKarma?.usdt ?? 0),
        dataQa: 'profile-balance-data-usdt',
      },
      {
        description: (
          <BrandIcon
            size="extra-small"
            icon={MonoSqr}
            iconColor="quaternary"
            label="SQRp"
            labelColor="tertiary"
          />
        ),
        value: formatReward(userKarma?.totalSqrp ?? 0),
        dataQa: 'profile-balance-data-sqrp',
      },
      {
        description: (
          <BrandIcon
            size="extra-small"
            icon={MonoSqr}
            iconColor="quaternary"
            label="SQRv"
            labelColor="tertiary"
          />
        ),
        value: formatReward(userSqrv?.score ?? 0),
        dataQa: 'profile-balance-data-sqrv',
      },
      {
        description: (
          <BrandIcon
            size="extra-small"
            icon={Karma}
            iconColor="pink"
            label="Karma"
            labelColor="tertiary"
          />
        ),
        value: formatReward(user.attributes.karma ?? 0),
        dataQa: 'profile-balance-data-karma',
      },
    ];
  }, [user, userKarma, userSqrv]);

  return <PriceDetails items={items} isLoading={isLoading} />;
};
